import { Modal } from "@JohnLewisPartnership/jl-design-system/dist/components/modal/Modal";
import { useCallback, useEffect } from "react";
import { useVariant } from "@~/contexts/VariantContext/VariantContext.tsx";
import { Product } from "@~/types/types.ts";
import { getAnalyticsData } from "@~/product-card-components/__shared__/QuickViewModal/utils.ts";
import { useExperienceString, useFeatureFlags } from "@~/flags/flags";
import { useProductCardEvent } from "@~/contexts/ProductCardEventContext/ProductCardEventContext";
import { productCardEvents } from "@~/contexts/ProductCardEventContext/productCardEvents";

declare global {
  // eslint-disable-next-line @typescript-eslint/no-namespace
  namespace React.JSX {
    export interface IntrinsicElements {
      "quick-view": {
        "data-testid"?: string;
        config?: string;
        "pim-product-id"?: string;
        "initial-variant-id"?: string;
        "analytics-additional-info"?: string;
        "experiences-raw-string"?: string;
        flags?: string;
      };
    }
  }
}

export type QuickViewProps = {
  product: Product;
  showModal: boolean;
  config?: string;
  modalTitle?: string;
  onClose: () => void;
  originator:
    | "QUICK_VIEW_ICON"
    | "VIEW_OFFERS"
    | "ADD_TO_BASKET"
    | "PRODUCT_CARD"
    | undefined;
};

type QuickViewEventDetail = {
  variantId?: string;
  sku?: string;
  qty?: number;
};

function getQuickViewEventOverrides(detail?: QuickViewEventDetail) {
  return {
    ...(detail?.variantId != null && { variantId: detail.variantId }),
    ...(detail?.sku != null && { skuId: detail.sku }),
    ...(detail?.qty != null && { quantity: detail.qty }),
  };
}

export function QuickViewModal({
  product,
  showModal,
  config = "standard",
  modalTitle = "",
  onClose,
  originator,
}: QuickViewProps) {
  const { variant } = useVariant();
  const experiencesRawString = useExperienceString();
  const flags = useFeatureFlags();

  const dispatchCardEvent = useProductCardEvent();

  const isQuickViewEventFromCurrentProduct = useCallback(
    (event: Event) => {
      return (
        showModal &&
        (event as CustomEvent).detail?.pimProductId ===
          product.aliases.pimProductId
      );
    },
    [showModal, product.aliases.pimProductId],
  );

  const dispatchQuickViewOpenedEvent = useCallback(
    (event: Event) => {
      if (isQuickViewEventFromCurrentProduct(event)) {
        const detail = (event as CustomEvent<QuickViewEventDetail>).detail;

        dispatchCardEvent(productCardEvents.quickViewOpened, {
          ...getQuickViewEventOverrides(detail),
          analyticsInfo: detail,
          originator,
        });
      }
    },
    [dispatchCardEvent, isQuickViewEventFromCurrentProduct, originator],
  );

  const dispatchQuickViewAddToBasketEvent = useCallback(
    (event: Event) => {
      if (showModal) {
        const detail = (event as CustomEvent<QuickViewEventDetail>).detail;

        dispatchCardEvent(productCardEvents.quickViewAddToBasket, {
          ...getQuickViewEventOverrides(detail),
          analyticsInfo: detail,
        });
      }
    },
    [dispatchCardEvent, showModal],
  );

  const dispatchQuickViewUpdatedEvent = useCallback(
    (event: Event) => {
      if (isQuickViewEventFromCurrentProduct(event)) {
        const detail = (event as CustomEvent<QuickViewEventDetail>).detail;

        dispatchCardEvent(productCardEvents.quickViewUpdated, {
          ...getQuickViewEventOverrides(detail),
          analyticsInfo: detail,
        });
      }
    },
    [dispatchCardEvent, isQuickViewEventFromCurrentProduct],
  );

  useEffect(function loadQuickViewScript() {
    if (
      !window.customElements.get("quick-view") &&
      !document.getElementById("jl-quick-view-script")
    ) {
      const script = document.createElement("script");
      script.src = "/quick-view/quick-view.iife.js";
      script.id = "jl-quick-view-script";
      document.head.appendChild(script);
    }
  }, []);

  useEffect(
    function subscribeQuickViewEvents() {
      window.addEventListener(
        "jl.quick-view.opened",
        dispatchQuickViewOpenedEvent,
      );
      window.addEventListener(
        "jl.quick-view.addToBasketSuccess",
        dispatchQuickViewAddToBasketEvent,
      );
      window.addEventListener(
        "jl.quick-view.colourSelected",
        dispatchQuickViewUpdatedEvent,
      );
      window.addEventListener(
        "jl.quick-view.sizeSelected",
        dispatchQuickViewUpdatedEvent,
      );
      return () => {
        window.removeEventListener(
          "jl.quick-view.opened",
          dispatchQuickViewOpenedEvent,
        );
        window.removeEventListener(
          "jl.quick-view.addToBasketSuccess",
          dispatchQuickViewAddToBasketEvent,
        );
        window.removeEventListener(
          "jl.quick-view.colourSelected",
          dispatchQuickViewUpdatedEvent,
        );
        window.removeEventListener(
          "jl.quick-view.sizeSelected",
          dispatchQuickViewUpdatedEvent,
        );
      };
    },
    [
      dispatchQuickViewOpenedEvent,
      dispatchQuickViewAddToBasketEvent,
      dispatchQuickViewUpdatedEvent,
    ],
  );

  useEffect(
    function handleQuickViewEvents() {
      window.addEventListener("jl.quick-view.exit", onClose);
      window.addEventListener("jl.route.change", onClose);

      return function unsubscribeFromQuickViewEvents() {
        window.removeEventListener("jl.quick-view.exit", onClose);
        window.removeEventListener("jl.route.change", onClose);
      };
    },
    [onClose],
  );

  const analyticsData = getAnalyticsData(product, originator);

  return (
    <Modal
      isOpen={showModal}
      footer={false}
      onClose={onClose}
      width="extra-large"
      height="fixed"
      title={modalTitle}
      disablePortal={true}
    >
      {showModal && (
        <quick-view
          data-testid="quick-view"
          config={config}
          pim-product-id={product.aliases.pimProductId}
          initial-variant-id={variant?.id}
          analytics-additional-info={JSON.stringify(analyticsData)}
          experiences-raw-string={experiencesRawString}
          flags={JSON.stringify(flags)}
        ></quick-view>
      )}
    </Modal>
  );
}
